import { portalClient } from '@/lib/portalClient';

/**
 * Typed client for the read-mostly external client portal (epic 128).
 * Backend: internal/portal (Go). Every authed read is scoped server-side to the
 * single account in the token's `acct` claim — the client never passes an
 * account id, so there is no IDOR surface here.
 */

export interface PortalLoginRequest {
  email: string;
  password: string;
}

export interface PortalLoginResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  tenantId: string;
  accountId: string;
}

export interface PortalAccount {
  id: string;
  name: string;
  industry: string | null;
  status: string | null;
  lifecycleStage: string | null;
  healthScore: number | null;
  website: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
}

export interface PortalOpportunity {
  id: string;
  name: string;
  status: string;
  estimatedDealValue: number | null;
  currency: string | null;
  expectedCloseDate: string | null;
  progressPercent: number | null;
  forecastClassification: string | null;
  updatedAt: string;
}

export interface PortalAppointment {
  id: string;
  attendeeName: string | null;
  status: string;
  meetingLink: string | null;
  bookedAt: string | null;
  confirmedAt: string | null;
  completedAt: string | null;
  opportunityId: string;
  opportunityName: string;
}

export interface PortalDocument {
  id: string;
  documentType: string;
  title: string;
  status: string;
  createdAt: string;
  opportunityId: string;
  opportunityName: string;
}

export interface PortalAgreement {
  id: string;
  kind: string;
  title: string;
  status: string;
  effectiveDate: string | null;
  expirationDate: string | null;
  signedDate: string | null;
}

export interface PortalBranding {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  fontFamily: string | null;
  customCss: string | null;
  customDomain: string | null;
  themeTokens?: { light?: Record<string, string>; dark?: Record<string, string> } | null;
}

export async function portalLogin(req: PortalLoginRequest): Promise<PortalLoginResponse> {
  const { data } = await portalClient.post<PortalLoginResponse>('/portal/login', {
    email: req.email.trim().toLowerCase(),
    password: req.password,
  });
  return data;
}

export async function getPortalAccount(): Promise<PortalAccount> {
  const { data } = await portalClient.get<PortalAccount>('/portal/account');
  return data;
}

export async function listPortalOpportunities(): Promise<PortalOpportunity[]> {
  const { data } = await portalClient.get<PortalOpportunity[]>('/portal/opportunities');
  return data ?? [];
}

export async function listPortalAppointments(): Promise<PortalAppointment[]> {
  const { data } = await portalClient.get<PortalAppointment[]>('/portal/appointments');
  return data ?? [];
}

export async function listPortalDocuments(): Promise<PortalDocument[]> {
  const { data } = await portalClient.get<PortalDocument[]>('/portal/documents');
  return data ?? [];
}

export async function listPortalAgreements(): Promise<PortalAgreement[]> {
  const { data } = await portalClient.get<PortalAgreement[]>('/portal/agreements');
  return data ?? [];
}

/**
 * Per-tenant branding for white-labeling the portal shell (story 128.2). The
 * `/branding` endpoint is authenticated-but-not-role-gated, so a CLIENT_VIEWER
 * token reads its own tenant's branding (tenant-scoped by RLS via the `tid`
 * claim) — letting the portal render as the operator's product, not Kairos.
 */
export async function getPortalBranding(): Promise<PortalBranding> {
  const { data } = await portalClient.get<PortalBranding>('/branding');
  return data;
}
